import React from 'react';
import styled from 'styled-components';
import Grow from '@material-ui/core/Grow';
import { DSClickAwayListener } from '../../../mui/ClickAwayListener/DSClickAwayListener';
import { DSPaper } from '../../../mui/Paper/DSPaper';
import { DSPopperProps, DSPopper } from '../../../mui/Popper/DSPopper';
import { ForceReRenderableProps, useForceRerender } from './useForceRerender';

const StyledPopper = styled(DSPopper)`
    z-index: ${props => props.theme.zIndex.tooltip};
`;

const StyledContainer = styled(DSPaper)<{ $width?: number; $noPadding: boolean }>`
    ${props => props.$width && `width: ${props.$width}px;`}
    box-shadow: ${props => props.theme.shadows[2]};

    ${props =>
        !props.$noPadding &&
        `
        padding: 5px;
    `}
`;

// Use this to remove the annoying flippy dropdown
const defaultPopperModifiers = {
    flip: { enabled: false },
};

export type InputDropdownProps = ForceReRenderableProps & {
    children: React.ReactNode;
    disablePortal?: boolean;
    modifiers?: DSPopperProps['modifiers'];
    noPadding?: boolean;
    onClickAway?: () => void;
    open: boolean;
    popperAnchorEl: HTMLElement | null;
    zIndex?: number;
};

export function InputDropdown({
    children,
    disablePortal,
    modifiers = defaultPopperModifiers,
    noPadding = false,
    onClickAway,
    open,
    popperAnchorEl,
    zIndex,
}: InputDropdownProps): JSX.Element {
    const popperWidth = popperAnchorEl?.clientWidth;

    const handleClickAway = React.useCallback(
        (event: React.MouseEvent<Document>) => {
            if (event.target instanceof Node && popperAnchorEl?.contains(event.target)) {
                return;
            }
            onClickAway?.();
        },
        [popperAnchorEl, onClickAway],
    );

    return (
        <StyledPopper
            anchorEl={popperAnchorEl}
            disablePortal={disablePortal}
            modifiers={modifiers}
            open={open}
            placement="bottom"
            role="menu"
            style={{
                zIndex,
            }}
            transition
        >
            {({ TransitionProps }) => (
                <Grow style={{ transformOrigin: 'center top' }} {...TransitionProps}>
                    <StyledContainer $noPadding={noPadding} $width={popperWidth}>
                        <DSClickAwayListener onClickAway={handleClickAway}>
                            <div>{children}</div>
                        </DSClickAwayListener>
                    </StyledContainer>
                </Grow>
            )}
        </StyledPopper>
    );
}
InputDropdown.displayName = 'InputDropdownContainer';
InputDropdown.useForceRerender = useForceRerender;
